import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Ticket, TicketStatus, TicketPriority, TicketQueue } from '@runninglime/shared';
import { TICKET_STATUS_LABELS, TICKET_QUEUE_LABELS } from '@runninglime/shared';
import { apiGet, apiPut } from '../../lib/api.ts';
import { formatDate } from '../../lib/utils.ts';
import { StatusBadge, PriorityBadge, QueueBadge, LoadingSpinner } from '../../components/ui/Shared.tsx';

const STATUS_TABS: { value: TicketStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in-progress-l1', label: 'L1' },
  { value: 'in-progress-l2', label: 'L2' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const QUEUE_TABS: { value: TicketQueue | ''; label: string }[] = [
  { value: '', label: 'All Queues' },
  { value: 'general', label: 'General' },
  { value: 'billing', label: 'Billing' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'product-issue', label: 'Product' },
  { value: 'returns', label: 'Returns' },
  { value: 'technical', label: 'Technical' },
];

export default function TicketManager() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [queueFilter, setQueueFilter] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (queueFilter) params.set('queue', queueFilter);
    const q = params.toString() ? `?${params.toString()}` : '';
    const res = await apiGet<{ items: Ticket[]; total: number }>(`/admin/tickets${q}`);
    if (res.success && res.data) setTickets(res.data.items);
    setLoading(false);
  };

  useEffect(() => { load(); }, [statusFilter, queueFilter]);

  const changeStatus = async (ticketId: string, status: TicketStatus) => {
    setUpdating(ticketId);
    await apiPut(`/admin/tickets/${ticketId}/status`, { status });
    await load();
    setUpdating(null);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="animate-fade-in">
      <h1 className="text-3xl font-bold text-dark mb-6">Support Tickets</h1>

      {/* Status filter */}
      <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              statusFilter === tab.value
                ? 'bg-lime text-dark shadow-md'
                : 'bg-white text-gray-600 hover:bg-lime/20 border border-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Queue filter */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {QUEUE_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setQueueFilter(tab.value)}
            className={`px-3 py-1.5 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${
              queueFilter === tab.value
                ? 'bg-dark text-white shadow-md'
                : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {tickets.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-400 text-sm">No tickets found</div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Queue</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Assigned</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Msgs</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Updated</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tickets.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50/50 transition">
                    <td className="px-6 py-4">
                      <Link to={`/admin/tickets/${t.id}`} className="font-medium text-dark hover:text-green transition">
                        {t.subject}
                      </Link>
                      <div className="text-xs text-gray-400 mt-0.5">{t.companyName}</div>
                    </td>
                    <td className="px-6 py-4"><QueueBadge queue={t.queue} /></td>
                    <td className="px-6 py-4"><PriorityBadge priority={t.priority} /></td>
                    <td className="px-6 py-4"><StatusBadge status={t.status} /></td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {t.assignment ? t.assignment.assignedToName : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{t.messages.length}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(t.updatedAt)}</td>
                    <td className="px-6 py-4 text-right">
                      <select
                        value={t.status}
                        onChange={(e) => changeStatus(t.id, e.target.value as TicketStatus)}
                        disabled={updating === t.id}
                        className="text-xs px-2 py-1 rounded-lg border border-gray-200 bg-white text-gray-600 disabled:opacity-50"
                      >
                        <option value="open">Open</option>
                        <option value="in-progress-l1">In Progress (L1)</option>
                        <option value="in-progress-l2">In Progress (L2)</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
